import React from 'react';
import { CancelToken, isCancel } from 'axios';

/**
 * When a component unmounts, we need to cancel any potentially
 * ongoing Axios calls that result in a state update on success / fail.
 * This function sets up the appropriate useEffect to handle the canceling.
 * This hook has been modified to also allow manual requests cancellation.
 * https://dev.to/tmns/usecanceltoken-a-custom-react-hook-for-cancelling-axios-requests-1ia4
 *
 * @returns {newCancelToken: function, cancelPreviousRequest: function, isCancel: function}
 * newCancelToken - used to generate the cancel token sent in the Axios request.
 * cancelPreviousRequest - used to manually cancel previous Axios requests.
 * isCancel - used to check if error returned in response is a cancel token error.
 */
export const useCancelToken = () => {
  const axiosSource = React.useRef(null);
  const newCancelToken = () => {
    axiosSource.current = CancelToken.source();
    return axiosSource.current.token;
  };

  const cancelPreviousRequest = () => {
    if (axiosSource.current) axiosSource.current.cancel();
  };

  React.useEffect(() => cancelPreviousRequest, []);

  return { newCancelToken, cancelPreviousRequest, isCancel };
};
